/**
 * Grade & Classification Utility based on standard grading scale:
 * 85 - 100 : A (Distinction Pass)
 * 75 - 84  : B (Merit Pass)
 * 65 - 74  : C (Credit Pass)
 * 50 - 64  : S (Ordinary Pass)
 * 0 - 49   : F (Not Qualified)
 */
export const GRADING_RUBRIC_SCALE = [
  { min: 85, max: 100, grade: 'A', classification: 'Distinction Pass', color: '#52c41a', tagColor: 'success' },
  { min: 75, max: 84, grade: 'B', classification: 'Merit Pass', color: '#1890ff', tagColor: 'processing' },
  { min: 65, max: 74, grade: 'C', classification: 'Credit Pass', color: '#722ed1', tagColor: 'purple' },
  { min: 50, max: 64, grade: 'S', classification: 'Ordinary Pass', color: '#fa8c16', tagColor: 'warning' },
  { min: 0, max: 49, grade: 'F', classification: 'Not Qualified', color: '#f5222d', tagColor: 'error' },
];

export const getGradeDetails = (rawMarks) => {
  if (rawMarks === null || rawMarks === undefined || rawMarks === '' || isNaN(Number(rawMarks))) {
    return {
      grade: 'N/A',
      classification: 'Ungraded',
      color: '#8c8c8c',
      tagColor: 'default',
      isGraded: false,
      marks: null,
    };
  }

  const marks = Number(rawMarks);
  const matched = GRADING_RUBRIC_SCALE.find(item => marks >= item.min && marks <= item.max) ||
                  (marks >= 85 ? GRADING_RUBRIC_SCALE[0] : GRADING_RUBRIC_SCALE[GRADING_RUBRIC_SCALE.length - 1]);

  return {
    grade: matched.grade,
    classification: matched.classification,
    color: matched.color,
    tagColor: matched.tagColor,
    isGraded: true,
    marks,
  };
};

/**
 * Calculates student performance across all assessments for a specific course.
 * @param {Array} assessments - List of assessments for the course (including attempt data)
 * @returns {Object} Course performance summary
 */
export const calculateCoursePerformance = (assessments = []) => {
  const totalCount = assessments.length;
  let attemptedCount = 0;
  let gradedCount = 0;
  let totalMarks = 0;

  assessments.forEach((ass) => {
    const attempt = ass?.attempt;
    if (attempt && attempt.id) {
      attemptedCount += 1;
      const rawMarks = attempt.marks;
      if (rawMarks !== null && rawMarks !== undefined && rawMarks !== '' && !isNaN(Number(rawMarks))) {
        gradedCount += 1;
        totalMarks += Number(rawMarks);
      }
    }
  });

  if (gradedCount === 0) {
    return {
      totalCount,
      attemptedCount,
      gradedCount,
      averageMarks: null,
      grade: null,
      classification: null,
      color: '#8c8c8c',
      tagColor: 'default',
      isGraded: false,
      isFullyGraded: false,
      summaryText: totalCount === 0 ? 'No Assignments' : 'Pending Evaluation',
    };
  }

  const averageMarks = Math.round((totalMarks / gradedCount) * 10) / 10;
  const gradeDetails = getGradeDetails(averageMarks);
  const isFullyGraded = gradedCount === totalCount && totalCount > 0;

  return {
    totalCount,
    attemptedCount,
    gradedCount,
    averageMarks,
    grade: gradeDetails.grade,
    classification: gradeDetails.classification,
    color: gradeDetails.color,
    tagColor: gradeDetails.tagColor,
    isGraded: true,
    isFullyGraded,
    summaryText: isFullyGraded
      ? `Final: ${gradeDetails.classification} (${gradeDetails.grade})`
      : `Current: ${gradeDetails.classification} (${gradeDetails.grade}) • ${gradedCount}/${totalCount} Evaluated`,
  };
};

/**
 * Assessment Types
 */
export const ASSESSMENT_TYPES = {
  CA: 'CA',
  FINAL_EXAM: 'FINAL_EXAM',
};

/**
 * Get display info for assessment type
 */
export const getAssessmentTypeDetails = (type = 'CA') => {
  const normType = (type || 'CA').toUpperCase();
  if (normType === 'FINAL_EXAM') {
    return {
      type: 'FINAL_EXAM',
      label: 'Final Examination',
      shortLabel: 'Final Exam',
      tagColor: 'gold',
      badgeColor: '#d97706',
      badgeBg: '#fef3c7',
      isFinalExam: true,
    };
  }
  return {
    type: 'CA',
    label: 'Continuous Assessment',
    shortLabel: 'CA',
    tagColor: 'cyan',
    badgeColor: '#0284c7',
    badgeBg: '#e0f2fe',
    isFinalExam: false,
  };
};

/**
 * Determine Final Exam eligibility based on Continuous Assessments (CA).
 * Rule: Student must get average >= 50% across all given CAs.
 * @param {Array} caAssessments - Array of CA assessments with attempt data
 * @returns {Object} Eligibility info
 */
export const calculateCAEligibility = (caAssessments = []) => {
  const totalCAs = caAssessments.length;

  if (totalCAs === 0) {
    return {
      totalCAs: 0,
      gradedCAs: 0,
      caAverage: null,
      isEligible: true,
      status: 'ELIGIBLE',
      badgeColor: '#52c41a',
      tagColor: 'success',
      label: 'Eligible (No CAs)',
      reason: 'No Continuous Assessments required for this course.',
    };
  }

  let gradedCAs = 0;
  let totalMarks = 0;

  caAssessments.forEach((ass) => {
    const marks = ass?.attempt?.marks;
    if (marks !== null && marks !== undefined && marks !== '' && !isNaN(Number(marks))) {
      gradedCAs += 1;
      totalMarks += Number(marks);
    }
  });

  const caAverage = gradedCAs > 0 ? Math.round((totalMarks / gradedCAs) * 10) / 10 : null;

  if (gradedCAs < totalCAs) {
    return {
      totalCAs,
      gradedCAs,
      caAverage,
      isEligible: false,
      status: 'PENDING',
      badgeColor: '#fa8c16',
      tagColor: 'warning',
      label: 'Pending CA Completion',
      reason: `Awaiting completion of all Continuous Assessments (${gradedCAs}/${totalCAs} evaluated). All CAs must be graded to verify eligibility.`,
    };
  }

  // All CAs are graded
  if (caAverage !== null && caAverage >= 50) {
    return {
      totalCAs,
      gradedCAs,
      caAverage,
      isEligible: true,
      status: 'ELIGIBLE',
      badgeColor: '#52c41a',
      tagColor: 'success',
      label: 'Eligible for Final Exam',
      reason: `Eligible: Continuous Assessment average is ${caAverage}% (Required: ≥ 50%).`,
    };
  }

  return {
    totalCAs,
    gradedCAs,
    caAverage: caAverage !== null ? caAverage : 0,
    isEligible: false,
    status: 'NOT_ELIGIBLE',
    badgeColor: '#f5222d',
    tagColor: 'error',
    label: 'Not Eligible',
    reason: `Ineligible: Continuous Assessment average is ${caAverage || 0}%, which is below the minimum required 50%.`,
  };
};

/**
 * Calculates complete course performance breaking down CAs and Final Exam,
 * checking eligibility, and deriving final overall course composite mark and classification.
 * @param {Array} assessments - All assessments for the course
 * @returns {Object} Comprehensive evaluation breakdown
 */
export const calculateCourseAssessmentBreakdown = (assessments = []) => {
  const caList = assessments.filter(
    (a) => (a?.assessmentType || 'CA').toUpperCase() !== 'FINAL_EXAM'
  );
  const finalExamList = assessments.filter(
    (a) => (a?.assessmentType || 'CA').toUpperCase() === 'FINAL_EXAM'
  );

  const caEligibility = calculateCAEligibility(caList);
  const caPerformance = calculateCoursePerformance(caList);
  const finalPerformance = calculateCoursePerformance(finalExamList);

  const hasCA = caList.length > 0;
  const hasFinalExam = finalExamList.length > 0;

  let overallMark = null;
  let isFullyCompleted = false;
  let overallGradeDetails = {
    grade: 'N/A',
    classification: 'Ungraded',
    color: '#8c8c8c',
    tagColor: 'default',
  };
  let summaryText = 'Pending Evaluation';

  if (!hasCA && !hasFinalExam) {
    summaryText = 'No Assessments';
  } else if (hasCA && !hasFinalExam) {
    // Only CAs
    if (caPerformance.isFullyGraded) {
      overallMark = caPerformance.averageMarks;
      isFullyCompleted = true;
      overallGradeDetails = getGradeDetails(overallMark);
      summaryText = `Final Classification: ${overallGradeDetails.classification} (Grade ${overallGradeDetails.grade})`;
    } else if (caPerformance.isGraded) {
      overallGradeDetails = getGradeDetails(caPerformance.averageMarks);
      summaryText = `Running: ${overallGradeDetails.classification} (${caPerformance.gradedCount}/${caPerformance.totalCount} CAs evaluated)`;
    }
  } else if (!hasCA && hasFinalExam) {
    // Only Final Exam
    if (finalPerformance.isFullyGraded) {
      overallMark = finalPerformance.averageMarks;
      isFullyCompleted = true;
      overallGradeDetails = getGradeDetails(overallMark);
      summaryText = `Final Classification: ${overallGradeDetails.classification} (Grade ${overallGradeDetails.grade})`;
    } else if (finalPerformance.isGraded) {
      overallGradeDetails = getGradeDetails(finalPerformance.averageMarks);
      summaryText = `Final Exam: ${overallGradeDetails.classification}`;
    }
  } else {
    // Both CAs and Final Exam exist!
    if (caEligibility.status === 'NOT_ELIGIBLE') {
      // Ineligible for Final Exam
      isFullyCompleted = true;
      overallMark = caEligibility.caAverage;
      overallGradeDetails = {
        grade: 'F',
        classification: 'Fail (Ineligible for Final Exam)',
        color: '#f5222d',
        tagColor: 'error',
      };
      summaryText = `Final: Fail (Ineligible for Final Exam - CA average ${caEligibility.caAverage}% < 50%)`;
    } else if (caEligibility.isEligible && finalPerformance.isFullyGraded) {
      // Completed all CAs (≥ 50%) AND Final Exam!
      // Composite Mark = (CA Average * 0.5) + (Final Exam Average * 0.5)
      overallMark = Math.round(((caEligibility.caAverage * 0.5) + (finalPerformance.averageMarks * 0.5)) * 10) / 10;
      isFullyCompleted = true;
      overallGradeDetails = getGradeDetails(overallMark);
      summaryText = `Final Classification: ${overallGradeDetails.classification} (Grade ${overallGradeDetails.grade}) • 50% CA + 50% Final Exam`;
    } else if (caEligibility.isEligible && !finalPerformance.isFullyGraded) {
      summaryText = 'Eligible for Final Exam • Awaiting Final Exam evaluation';
    } else {
      summaryText = `Awaiting CA completion (${caEligibility.gradedCAs}/${caEligibility.totalCAs} evaluated)`;
    }
  }

  return {
    totalAssessments: assessments.length,
    caList,
    finalExamList,
    hasCA,
    hasFinalExam,
    caEligibility,
    caPerformance,
    finalPerformance,
    overallMark,
    isFullyCompleted,
    overallGrade: overallGradeDetails.grade,
    overallClassification: overallGradeDetails.classification,
    overallColor: overallGradeDetails.color,
    overallTagColor: overallGradeDetails.tagColor,
    summaryText,
  };
};
